import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Circle, Square, X } from "lucide-react";
import { Modal } from "../../../../shared/ui/modal";
import { Ambiance } from "../../domain/types";
import {
    AMBIANCE_FLOOR,
    AMBIANCES,
    DEFAULT_AMBIANCE,
    DEFAULT_TABLE_COLOR,
    getBoardDefaults,
    setBoardDefaults,
} from "../../application/boardDefaults";

type Props = { isOpen: boolean; onClose: () => void };

/** Therapist sets the default table shape, floor color and scene for new
 *  boards (issue #197). Persisted in localStorage and applied when a fresh
 *  board opens. */
export default function BoardDefaultsModal({ isOpen, onClose }: Props) {
    const { t } = useTranslation();
    const [shape, setShape] = useState<"circle" | "square">("circle");
    const [color, setColor] = useState<string>(DEFAULT_TABLE_COLOR);
    const [ambiance, setAmbiance] = useState<Ambiance>(DEFAULT_AMBIANCE);

    // Load the saved defaults each time the modal opens.
    useEffect(() => {
        if (isOpen) {
            const d = getBoardDefaults();
            setShape(d.shape);
            setColor(d.color);
            setAmbiance(d.ambiance);
        }
    }, [isOpen]);

    const save = () => {
        setBoardDefaults({ shape, color, ambiance });
        onClose();
    };

    const shapeBtn = (value: "circle" | "square", icon: React.ReactNode, text: string) => (
        <button
            type="button"
            onClick={() => setShape(value)}
            aria-pressed={shape === value}
            className={`flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-md border px-3 text-sm ${
                shape === value
                    ? "border-brand-500 bg-brand-50 ring-2 ring-brand-200"
                    : "border-gray-200 hover:border-brand-200"
            }`}
        >
            {icon} {text}
        </button>
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="mx-4 max-w-sm rounded-2xl">
            <div className="p-6">
                <div className="mb-1 flex items-center justify-between">
                    <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                        {t("constellation.boardDefaults.title")}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t("common.close", "Close")}
                        className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100"
                    >
                        <X size={16} />
                    </button>
                </div>
                <p className="mb-4 text-xs text-gray-500">
                    {t("constellation.boardDefaults.hint")}
                </p>

                <p className="mb-1 text-xs font-semibold text-gray-500">
                    {t("constellation.table.shape")}
                </p>
                <div className="mb-4 flex gap-2">
                    {shapeBtn("circle", <Circle size={16} />, t("constellation.table.circle"))}
                    {shapeBtn("square", <Square size={16} />, t("constellation.table.square"))}
                </div>

                <div className="mb-2 flex items-center justify-between">
                    <p className="text-xs font-semibold text-gray-500">
                        {t("constellation.table.color")}
                    </p>
                    <input
                        type="color"
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                        aria-label={t("constellation.table.color")}
                        className="h-9 w-14 cursor-pointer rounded border bg-white"
                    />
                </div>

                <p className="mb-1 text-xs font-semibold text-gray-500">
                    {t("constellation.ambiance.label")}
                </p>
                <select
                    value={ambiance}
                    onChange={(e) => {
                        const next = e.target.value as Ambiance;
                        setAmbiance(next);
                        // A scene carries its own floor, same as the in-board
                        // style picker, so the two never drift apart.
                        const floor = AMBIANCE_FLOOR[next];
                        if (floor) setColor(floor);
                    }}
                    aria-label={t("constellation.ambiance.label")}
                    className="field-base field-default field-height"
                >
                    {AMBIANCES.map((a) => (
                        <option key={a} value={a}>
                            {t(`constellation.ambiance.${a}`)}
                        </option>
                    ))}
                </select>

                <div className="mt-5 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md border px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
                    >
                        {t("constellation.scenes.cancel")}
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
                    >
                        {t("constellation.scenes.confirm")}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
